export const MAP_TITLE_UPDATE_CHANNEL = 'mindmapi-map-title-updates-v1';
export const MAP_TITLE_UPDATE_EVENT = 'mindmapi:map-title-updated';

function normalizeUpdate(update) {
  if (!update || typeof update.mapId !== 'string') return null;
  const mapId = update.mapId.trim();
  if (!mapId || typeof update.title !== 'string') return null;
  return { mapId, title: update.title.trim() };
}

export function publishMapTitleUpdate(update) {
  const normalized = normalizeUpdate(update);
  if (!normalized || typeof window === 'undefined') return;

  try {
    window.dispatchEvent(new CustomEvent(MAP_TITLE_UPDATE_EVENT, {
      detail: normalized,
    }));
  } catch {
    // Cross-tab delivery below remains available when CustomEvent is unavailable.
  }

  if (typeof window.BroadcastChannel !== 'function') return;
  try {
    const channel = new window.BroadcastChannel(MAP_TITLE_UPDATE_CHANNEL);
    channel.postMessage(normalized);
    channel.close();
  } catch {
    // Same-window delivery above remains available when cross-tab delivery fails.
  }
}

function jsonValuesEqual(left, right) {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => jsonValuesEqual(value, right[index]));
  }

  if (
    !left
    || !right
    || typeof left !== 'object'
    || typeof right !== 'object'
  ) {
    return false;
  }

  const leftKeys = Object.keys(left)
    .filter((key) => left[key] !== undefined)
    .sort();
  const rightKeys = Object.keys(right)
    .filter((key) => right[key] !== undefined)
    .sort();

  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => (
    key === rightKeys[index]
    && jsonValuesEqual(left[key], right[key])
  ));
}

export function subscribeToMapTitleUpdates(listener) {
  if (typeof listener !== 'function' || typeof window === 'undefined') {
    return () => {};
  }

  const handleWindowEvent = (event) => {
    const normalized = normalizeUpdate(event?.detail);
    if (normalized) listener(normalized);
  };
  const handleChannelMessage = (event) => {
    const normalized = normalizeUpdate(event?.data);
    if (normalized) listener(normalized);
  };

  window.addEventListener(MAP_TITLE_UPDATE_EVENT, handleWindowEvent);
  let channel = null;
  if (typeof window.BroadcastChannel === 'function') {
    try {
      channel = new window.BroadcastChannel(MAP_TITLE_UPDATE_CHANNEL);
      channel.addEventListener('message', handleChannelMessage);
    } catch {
      channel = null;
    }
  }

  return () => {
    window.removeEventListener(MAP_TITLE_UPDATE_EVENT, handleWindowEvent);
    if (channel) {
      channel.removeEventListener('message', handleChannelMessage);
      channel.close();
    }
  };
}

export function buildLinkedMapTitlePatch(node, { mapId, title, metadata = null, currentUserId = null }) {
  if (!node || typeof title !== 'string') return null;
  if (node.embeddedMindmapId && node.embeddedMindmapId !== mapId) return null;

  const nextTitle = title.trim();
  const currentText = typeof node.text === 'string' ? node.text.trim() : '';
  const previousTitle = node.linkMeta?.title
    ? String(node.linkMeta.title).trim()
    : null;
  const shouldSyncText =
    !currentText
    || currentText === 'New Map'
    || (previousTitle !== null && currentText === previousTitle);

  const nextLinkMeta = {
    ...(node.linkMeta || {}),
    ...(metadata || {}),
    title: nextTitle,
  };
  const shouldUpdateText = shouldSyncText && node.text !== nextTitle;
  if (!shouldUpdateText && jsonValuesEqual(node.linkMeta || {}, nextLinkMeta)) {
    return null;
  }

  const patch = {
    linkMeta: nextLinkMeta,
    lastEditedBy: currentUserId,
  };
  if (shouldUpdateText) {
    patch.text = nextTitle;
  }
  return patch;
}
